<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { messageStamp } from 'shared/helpers/timeHelper';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  firstReferral: {
    type: Object,
    required: true,
  },
  lastReferral: {
    type: Object,
    default: null,
  },
});

const { t } = useI18n();

const referrals = computed(() => {
  const items = [
    {
      key: 'first',
      title: t('CONTACT_PANEL.AD_ORIGIN.FIRST_AD'),
      ...props.firstReferral,
    },
  ];
  if (
    props.lastReferral &&
    props.lastReferral.message_id !== props.firstReferral.message_id
  ) {
    items.push({
      key: 'last',
      title: t('CONTACT_PANEL.AD_ORIGIN.LAST_AD'),
      ...props.lastReferral,
    });
  }
  return items;
});

const receivedAt = referral =>
  messageStamp(
    Math.floor(Date.parse(referral.received_at) / 1000),
    'LLL d yyyy, h:mm a'
  );

const copyAdId = async sourceId => {
  await copyTextToClipboard(sourceId);
  useAlert(t('CONTACT_PANEL.COPY_SUCCESSFUL'));
};
</script>

<template>
  <div class="flex flex-col gap-3 px-4 py-3 border-b border-n-weak">
    <span class="text-sm font-medium text-n-slate-12">
      {{ t('CONTACT_PANEL.AD_ORIGIN.TITLE') }}
    </span>
    <div
      v-for="referral in referrals"
      :key="referral.key"
      class="flex flex-col gap-1 text-sm"
    >
      <div class="flex gap-2 text-xs text-n-slate-11">
        <span>{{ referral.title }}</span>
        <span v-if="referral.received_at">{{ receivedAt(referral) }}</span>
      </div>
      <span v-if="referral.headline" class="text-n-slate-12">
        {{ referral.headline }}
      </span>
      <div v-if="referral.source_id" class="flex items-center gap-1">
        <span class="text-xs text-n-slate-11">
          {{ t('CONTACT_PANEL.AD_ORIGIN.AD_ID', { id: referral.source_id }) }}
        </span>
        <NextButton
          ghost
          slate
          xs
          icon="i-lucide-copy"
          :title="t('CONTACT_PANEL.AD_ORIGIN.COPY_AD_ID')"
          @click="copyAdId(referral.source_id)"
        />
      </div>
    </div>
  </div>
</template>
